<template>
  <div style="overflow-y: scroll; overflow-x: hidden; padding: 1px; height: 100%;">
    <div class="ui basic segment">
      <div class="ui create-publication info form">
        <h3 class="ui header">{{ $t('CREATE', { thing: $tc('PUBLICATION', 1) }) }}</h3>

        <div class="ui icon info message">
          <i :class="[(publicationType && publicationType.icon) + ' icon']" />
          <div class="content">
            <div class="header">{{ publicationType && publicationType.name }}</div>
            <div class="description" v-html="publicationType && publicationType.description" />
          </div>
        </div>

        <dropdown-select
          class="publication-type"
          :labelText="$t('PUBLICATION_TYPE')"
          :items="publicationTypes"
          @selected="selectPublicationType"
        />
        <dropdown-select
          class="hub"
          :labelText="$tc('HUB', 1)"
          :items="hubsItems"
          :optional="true"
          @selected="selectHub"
        />
      </div>
    </div>
    <div class="ui secondary menu">
      <div class="right floated item">
        <div class="ui mini buttons">
          <div class="ui green button" @click="add" :class="{ disabled: !this.selectedHub }">
            {{ $t('CREATE', { thing: $tc('PUBLICATION', 1) }) }}
          </div>
        </div>
      </div>
    </div>

    <h3 class="ui header capitalize-fl">{{ $tc('PUBLICATION', publications.length) }}</h3>

    <div class="ui error message" v-if="hubErr">
      <i class="exclamation circle icon" />
      {{ $t('ERR_LOADING_FROM_HUB', { err: hubErr }) }}
    </div>
    <div class="ui message" v-else-if="!publications || publications.length === 0">
      <i class="ui sad cry icon" />
      {{ $t('NOT_PUBLISHED', { thing: $tc('FLOW', 1) }) }}
    </div>

    <Spinny
      :promise="publicationsPromise"
      :message="$t('READ', { thing: $tc('PUBLICATION', 2) })"
    />

    <template v-for="pub in publications">
      <Publication
        :publication="pub"
        :hubs="hubs"
        :applications="applications"
        :licenses="licenses"
        :showAbstract="true"
        :categories="categories"
        v-if="!toPublish || toPublish.identifier !== pub.identifier"
        :key="'publication_' + pub.identifier"
      />
      <template v-else>
        <div
          class="ui top attached secondary inverted menu"
          :key="'header_' + pub.identifier"
          :class="{
            red: $v.$invalid || toPublish.contentErr,
            green: !($v.$invalid || toPublish.contentErr)
          }"
          v-if="toPublish && toPublish.identifier === pub.identifier"
        >
          <div class="item" v-if="!editPublication">
            {{
              $t('PUBLISH_NEW_HEADER', { type: publicationType.name, hub: toPublish.origin.name })
            }}
          </div>
          <div class="item" v-else>{{ $t('EDIT_PUBLICATION_HEADER') }}</div>
        </div>
        <div class="ui attached segment" :key="'editsegment_' + pub.identifier">
          <div>
            <div class="ui form">
              <div class="three fields">
                <!-- NAME -->
                <div class="field required" :class="{ error: $v.toPublish.name.$error }">
                  <label>{{ $t('NAME') }}</label>
                  <input
                    type="text"
                    name="name"
                    :placeholder="toPublish.name"
                    v-model="toPublish.name"
                    @click="$v.toPublish.name.$touch()"
                  />
                </div>

                <!-- CATEGORY -->
                <dropdown-select
                  :labelText="$tc('CATEGORY', 1)"
                  :items="categoryItems"
                  :initialSelection="toPublish.category"
                  :optional="true"
                  @selected="selectCategory"
                />

                <!-- APPLICATION -->
                <dropdown-select
                  :labelText="$tc('APPLICATION', 1)"
                  :items="applicationItems"
                  :optional="true"
                  :initialSelection="toPublish.application"
                  @selected="selectApplication"
                />
              </div>

              <!-- ABSTRACT -->
              <div class="field">
                <label>{{ $t('ABSTRACT') }}</label>
                <input
                  type="text"
                  name="name"
                  :placeholder="toPublish.abstract"
                  v-model="toPublish.abstract"
                />
              </div>
            </div>

            <!-- DESCRIPTION -->
            <markdown-editor
              :height="'200px'"
              ref="descriptionMarkdown"
              :label="$t('DESCRIPTION')"
              :value="toPublish.description"
              @dropped="dropFilesAtDescription"
            />

            <div class="ui form">
              <h4 class="ui horizontal header capitalize-fl">
                {{ $t('AUTHOR') }}
              </h4>
              <!-- AUTHOR -->
              <div class="two fields">
                <!-- NAME -->
                <div class="field">
                  <label>{{ $t('NAME_OF', { thing: $t('AUTHOR') }) }}</label>
                  <input
                    type="text"
                    name="name"
                    :placeholder="toPublish.author.name"
                    v-model="toPublish.author.name"
                  />
                </div>

                <!-- EMAIL -->
                <div class="field">
                  <label>{{ $t('EMAIL') }}</label>
                  <input
                    type="text"
                    name="name"
                    :placeholder="toPublish.author.email"
                    v-model="toPublish.author.email"
                  />
                </div>
              </div>
            </div>
            <!-- LICENSE -->
            <h4 class="ui horizontal header capitalize-fl">
              {{ $tc('LICENSE', 1) }}
            </h4>
            <div class="ui form">
              <!-- TEMPLATE SELECTION -->
              <dropdown-select
                :labelText="$t('LICENSE_TEMPLATE')"
                :items="licenseItems"
                @selected="selectLicense"
              />
              <!-- NAME -->
              <div class="field">
                <label>{{ $t('NAME_OF', { thing: $tc('LICENSE', 1) }) }}</label>
                <input
                  type="text"
                  name="name"
                  :placeholder="toPublish.license.name"
                  v-model="toPublish.license.name"
                />
              </div>
            </div>

            <markdown-editor
              :height="'200px'"
              ref="licenseMarkdown"
              :label="$t('DESCRIPTION_OF', { thing: $tc('LICENSE', 1) })"
              :value="toPublish.license.description"
            />

            <!-- ATTACHMENTS -->
            <div
              class="drop-parent"
              @dragover.stop.prevent="dragover"
              @dragenter.stop.prevent="dragenter"
              @dragexit.stop.prevent="dragexit"
              @dragleave.stop.prevent="dragexit"
              @drop.stop.prevent="drop"
            >
              <h4 class="ui horizontal header capitalize-fl">
                {{ $t('ATTACHMENTS') }}
              </h4>
              <div class="drop-target" :class="{ over: filesOver }" style="pointer-events: none;">
                <div class="drop-message">
                  {{ $t('DROP_HERE_TO_ATTACH') }}
                </div>
              </div>
              <kitchen-table
                v-if="page.totalItems > 0"
                :page="page"
                :columns="columns"
                :allowColumnSelection="true"
                :maxHeight="'100vh - 200px'"
              >
                <template #size="{ cell }">
                  {{ prettySize(parseInt(cell)) }}
                </template>
                <template #lastmodified="{ cell }">
                  {{ new Date(parseInt(cell) * 1000).toLocaleString() }}
                </template>
                <template #controls="{ row }">
                  <div class="ui red mini icon button" @click="delAttachment(row.filename)">
                    <i class="trash icon" />
                  </div>
                </template>
              </kitchen-table>
              <div v-else>{{ $t('NO_ATTACHMENTS') }}</div>
            </div>

            <div class="ui form">
              <!-- RELEASENOTE -->
              <h4 class="ui horizontal header capitalize-fl">
                {{ $t('RELEASENOTE') }}
              </h4>

              <div
                class="field required"
                :class="{ error: $v.toPublish.releaseNote.version.$error }"
              >
                <label>{{ $tc('VERSION', 1) }}</label>
                <input
                  type="text"
                  name="name"
                  :placeholder="toPublish.releaseNote.version"
                  v-model="toPublish.releaseNote.version"
                  :disabled="editPublication"
                  @click="$v.toPublish.releaseNote.version.$touch()"
                />
              </div>
            </div>
            <markdown-editor
              :required="true"
              :height="'200px'"
              ref="releaseMarkdown"
              :label="$t('RELEASENOTE')"
              :value="toPublish.releaseNote.note"
              @dropped="dropFilesAtReleaseNote"
            />
            <div v-if="toPublish.extensions && toPublish.type === 'INLINED_FLOW'">
              <h4 class="ui horizontal header capitalize-fl">
                {{ $tc('EXTENSION', 2) }}
              </h4>
              <p v-html="$t('EXTENSIONS_DESCRIPTION')"></p>
              <div class="ui two cards">
                <div class="card" v-for="e in toPublish.extensions" :key="e.type + '_' + e.name">
                  <div class="content">
                    <div class="header">{{ e.name }}</div>
                    <div class="meta">{{ e.type }}</div>
                    <div
                      class="params"
                      v-if="e.type === 'string'"
                      v-html="
                        $t('EXTENSION_STRING_DESCRIPTION', {
                          match: e.shouldMatch
                        })
                      "
                    />
                    <div
                      class="params"
                      v-else-if="e.type === 'selection'"
                      v-html="
                        $t('EXTENSION_SELECTION_DESCRIPTION', {
                          options: `<ul><li>${(
                            e.options || [$t('NO', { thing: $tc('OPTION', 2) })]
                          ).join('</li><li>')}</li></ul>`
                        })
                      "
                    />
                    <div
                      class="params"
                      v-else
                      v-html="
                        $t(
                          'EXTENSION_' +
                            (e.type && e.type.toUpperCase && e.type.toUpperCase()) +
                            '_DESCRIPTION'
                        )
                      "
                    />

                    <div class="description">
                      <markdown-editor
                        :height="'200px'"
                        :ref="e.type + '_' + e.name + '_description'"
                        :label="$t('DESCRIPTION_OF', { thing: e.name })"
                        :value="e.description"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="ui attached negative icon message"
          v-if="!$v.toPublish.name.required"
          :key="'validation_name_' + pub.identifier"
        >
          <i class="warning icon"></i>
          <div class="content">
            {{ $t('MISSING_NAME') }}
          </div>
        </div>
        <div
          class="ui attached negative icon message"
          v-if="!$v.toPublish.releaseNote.version.newVersion"
          :key="'validation_v_' + pub.identifier"
        >
          <i class="warning icon"></i>
          <div class="content">
            {{ $t('UPDATE_VERSION_ON_RELEASE_NOTE') }}
          </div>
        </div>
        <div
          class="ui attached negative icon message"
          v-if="toPublish.contentErr"
          :key="'validation_contentErr_' + pub.identifier"
        >
          <i class="code icon"></i>
          <div class="content">
            <p>{{ toPublish.contentErr.i18n }}</p>
            <pre
              class="monospaced context"
              v-html="toPublish.contentErr.context"
              v-if="toPublish.contentErr.context"
            />
          </div>
        </div>
      </template>
      <div
        class="publication-menu ui bottom attached secondary menu"
        :key="'menu_' + pub.identifier"
      >
        <div class="item">
          <div
            class="ui mini icon red button"
            :title="$t('DELETE', { thing: $tc('PUBLICATION', 1) })"
            :class="{ disabled: !pub.identifier }"
            v-confirm-click="{
              onConfirm: () => {
                del(pub)
              },
              confirmMessage: $t('DELETE_CONFIRM_MESSAGE', {
                thingType: $tc('PUBLICATION', 1),
                thingName: pub.name
              })
            }"
          >
            <i class="trash icon" />
          </div>
        </div>
        <div class="right floated item">
          <div class="ui mini buttons">
            <template v-if="!toPublish || toPublish.identifier !== pub.identifier">
              <div class="ui blue button" @click="edit(pub)">
                {{ $t('EDIT', { thing: $tc('PUBLICATION', 1), name: pub.name }) }}
              </div>
              <div
                class="ui green button"
                @click="publish(pub)"
                :class="{ disabled: editPublication || !hasChanged(pub) }"
              >
                {{ $t('PUBLISH_NEW') }}
              </div>
            </template>
            <template v-else>
              <div class="ui button" @click="cancel">{{ $t('CANCEL') }}</div>
              <div
                class="ui green button capitalize-fl"
                @click="save"
                :class="{ disabled: $v.$invalid || toPublish.contentErr }"
              >
                {{ $t('SAVE') }}
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.create-publication.form {
  display: grid;
  grid-template-rows: auto auto;
  grid-column-gap: 1em;
  grid-template-columns: 3fr 1fr;
  grid-template-areas:
    'headline headline'
    'message message'
    'publication-type hub';

  .header {
    grid-area: headline;
  }
  .message {
    grid-area: message;
  }
  .publication-type {
    grid-area: publication-type;
  }
  .hub {
    grid-area: hub;
  }
}

.card .params {
  margin-top: 1em;
  margin-bottom: 1em;
}

.publication-menu {
  margin-bottom: 50px !important;
}

pre.monospaced.context em {
  text-decoration: wavy underline;
  font-style: normal !important;
}

h4.header {
  margin-top: 4em !important;
  margin-bottom: 2em !important;
}

.message .content .header {
  margin-bottom: 1em;
}

.drop-parent {
  position: relative;

  .drop-target {
    z-index: 100;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: none;
    margin: auto;
    border-radius: 3px;

    &.over {
      display: flex;
      align-items: center;
      background-color: rgba(133, 183, 217, 0.9);
    }

    .drop-message {
      width: 400px;
      height: 50px;
      line-height: 50px;
      margin: auto;
    }
  }
}
</style>

<script>
// @flow
import Vue from 'vue'
import { mapState } from 'vuex'
import { prettySize } from 'utility'
import { Hub, Auth, Changelog, Toast, Flow } from 'services'
import _ from 'lodash'
import Publication from './Publication'
import { required } from 'vuelidate/lib/validators'
import {
  MarkdownEditor,
  KitchenTable,
  KitchenPage,
  InMemoryPager,
  DropdownSelect,
  Dropdown,
  Spinny
} from 'ui'

export default {
  name: 'flow-hub',

  props: ['flow'],

  components: { MarkdownEditor, KitchenTable, DropdownSelect, Publication, Spinny },

  validations() {
    const newVersion = value => value !== this.toPublishOldVersion
    return {
      toPublish: {
        name: { required },
        releaseNote: {
          version: {
            newVersion
          }
        }
      }
    }
  },

  data() {
    return {
      publicationsPromise: null,
      publications: [],
      addedFiles: [],
      attachedFiles: [],
      selectedHub: null,
      toPublish: null,
      toPublishOldVersion: '',
      toPublishContent: null,
      editPublication: false,
      filesOver: false,
      savePromise: null,
      change: null,

      publicationTypes: Hub.publicationTypes(),
      publicationType: Hub.publicationTypes()[0],

      columns: [
        {
          key: 'filename',
          name: this.$t('FILENAME'),
          sorts: true
        },
        {
          key: 'size',
          name: this.$t('SIZE'),
          sorts: true
        },
        {
          key: 'lastModified',
          name: this.$t('LAST_MODIFIED'),
          sorts: true
        },
        {
          key: 'controls',
          sorts: false
        }
      ]
    }
  },

  computed: {
    connections() {
      return this.$store.state.hub.connections
    },
    hubErr() {
      return this.$store.state.hub.loadingErr
    },
    hubs() {
      return this.$store.state.hub.hubs
    },
    categories() {
      return this.$store.state.hub.categories
    },
    applications() {
      return this.$store.state.hub.applications
    },
    licenses() {
      return this.$store.state.hub.licenses
    },
    currentUser() {
      const username = this.$store.state.currentUser || Auth.getLoggedInUser()
      return _.find(this.$store.state.user.items, u => u.name === username)
    },
    registryName() {
      return this.$store.state.config.name
    },
    appId() {
      return this.$route.params.appId
    },
    hubsItems() {
      return _.map(this.$store.state.hub.items, item => {
        const disabled = !_.find(_.values(this.hubs), h => h.hub.id === item.id)
        return {
          ...item,
          disabled,
          color: disabled ? 'red' : null,
          icon: disabled ? 'exclamation circle' : null,
          hint: disabled ? this.$i18n.t('HUB_UNREACHABLE') : null
        }
      })
    },
    categoryItems() {
      return _.map(
        // only show categories from the selected hub
        _.filter(this.categories, c => c.origin?.hub === this.toPublish.origin.hub),
        c => {
          return { id: c.identifier, ...c }
        }
      )
    },
    applicationItems() {
      return _.map(
        // only show applications from the selected hub
        _.filter(this.applications, a => a.origin?.hub === this.toPublish.origin.hub),
        a => {
          return { id: a.identifier, ...a }
        }
      )
    },
    licenseItems() {
      return _.map(this.licenses, l => {
        return { id: l.identifier, ...l }
      })
    },
    flows() {
      return this.$store.state.flow.items
    },
    fields() {
      return this.$store.state.field.items
    },
    page() {
      return new KitchenPage(
        new InMemoryPager({
          items: [...this.addedFiles, ...this.attachedFiles],
          pageSize: 50
        })
      )
    }
  },

  methods: {
    prettySize,

    hasChanged(p) {
      return this.change && p.origin && this.flow.version !== p.origin.version
    },

    del(p) {
      Hub.unpublish(this.connectionFor(p), p)
        .then(
          () =>
            (this.publications = _.filter(
              this.publications,
              pub => pub.identifier !== p.identifier
            ))
        )
        .then(this.reset)
        .then(() =>
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('PUBLICATION', 1) }))
        )
        .catch(() =>
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('WAS_NOT_DELETED', { thing: this.$i18n.tc('PUBLICATION', 1) })
            })
          )
        )
    },

    delAttachment(filename) {
      const conn = this.connectionFor(this.toPublish)
      if (_.find(this.addedFiles, f => f.filename === filename)) {
        this.addedFiles = _.reject(this.addedFiles, f => f.filename === filename)
      } else {
        Hub.deleteAttachment(conn, this.toPublish, filename).then(
          () => (this.attachedFiles = _.reject(this.attachedFiles, f => f.filename === filename))
        )
      }
    },

    selectHub(h) {
      this.selectedHub = h
    },

    selectLicense(l) {
      if (!l) return
      this.toPublish.license = { name: l.name, description: l.description }
    },

    selectPublicationType(t) {
      this.publicationType = t
    },

    selectCategory(c) {
      this.toPublish.category = c ? c.identifier : null
    },

    selectApplication(a) {
      this.toPublish.application = a ? a.identifier : null
    },

    connectionFor(p) {
      return _.find(this.connections, c => c.hub.id === this.hubs[p.origin.hub].hub.id)
    },

    publish(p) {
      this.toPublish = p
      this.toPublishOldVersion = this.toPublish.releaseNote.version
      this.editPublication = false
      const { content, extensions, err } = this.produceContent(this.toPublish.type)
      this.toPublishContent = content
      this.toPublish.contentErr = err
      this.toPublish.extensions = _.merge(
        _.sortBy(extensions, ['type', 'name']),
        _.sortBy(p.extensions, ['type', 'name'])
      )
    },

    add() {
      const selectedHub = _.isEmpty(this.selectedHub) ? _.head(this.hubsItems) : this.selectedHub
      const hub = _.find(_.values(this.hubs), h => h.hub.id === selectedHub.id)
      this.toPublish = {
        name: this.flow.name,
        identifier: this.flow.identifier,
        releaseNote: { version: '1.0', description: '', releasedAt: Date.now() },
        author: {
          name: this.currentUser.fullname || this.currentUser.name,
          email: this.currentUser.email
        },
        type: this.publicationType.id,
        license: { name: '', description: '' },
        origin: {
          hub: hub.identifier,
          relative: hub.identifier,
          name: hub.hub.name,
          identifier: this.flow.id,
          registry: this.registryName,
          url: window.location.href,
          change: this.change.identifier,
          version: this.flow.version
        },
        abstract: '',
        description: '',
        application: '',
        extensions: []
      }
      this.publications = [this.toPublish, ...this.publications]
      this.editPublication = false
      const { content, extensions, err } = this.produceContent(this.toPublish.type)
      this.toPublishContent = content
      this.toPublish.contentErr = err
      this.toPublish.extensions = extensions
    },

    edit(p) {
      this.toPublish = p
      this.editPublication = true
      // Load attachments
      Hub.attachmentsOf(this.connectionFor(p), p).then(
        atts => (this.attachedFiles = atts.body.attachments)
      )
    },

    cancel() {
      this.editPublication = false
      this.toPublish = null
      this.publications = _.filter(this.publications, p => !_.isEmpty(p.identifier))
    },

    reset() {
      this.toPublish = null
      return Hub.connections().then(conns =>
        Promise.allSettled(_.flatMap(conns, Hub.latest)).then(pubs => {
          console.debug('got pubs', JSON.stringify(pubs, null, 2))
          const items = _.flatMap(
            _.filter(pubs, p => p.status === 'fulfilled'),
            p => p.value && p.value.body && p.value.body.items
          )
          this.publications = _.uniqBy(
            _.filter(
              items,
              item => item.origin && item.origin.local && item.origin.identifier === this.flow.id
            ),
            item => item.identifier
          )
        })
      )
    },

    produceContent(publicationType) {
      try {
        switch (publicationType) {
          case Hub.FLOW_PUBLICATION:
            return { content: JSON.stringify({ ...this.flow }) }
          case Hub.INLINED_FLOW_PUBLICATION:
            const inlined = Hub.inline(
              this.flow.name,
              this.flow.code,
              this.flow.application,
              this.flows,
              this.fields
            )
            return {
              extensions: inlined.extensions,
              content: JSON.stringify({
                ...this.flow,
                ...inlined
              })
            }
        }
      } catch (err) {
        console.warn('error producing content', err)
        return { content: '', err }
      }
    },

    extensionsWithDescriptions(extensions) {
      return _.map(extensions, e => ({
        ...e,
        description: this.$refs[`${e.type}_${e.name}_description`][0].content()
      }))
    },

    save() {
      // Do not save if input does not validate
      if (this.$v.$invalid) {
        Toast.error(
          this.$i18n.t('ERR', {
            action: this.$i18n.t('VALIDATING')
          })
        )
        return
      }

      const conn = this.connectionFor(this.toPublish)

      // Get texts from editors
      this.toPublish.description = this.$refs.descriptionMarkdown[0].content()
      this.toPublish.releaseNote.note = this.$refs.releaseMarkdown[0].content()
      this.toPublish.license.description = this.$refs.licenseMarkdown[0].content()
      // ... and collect them from extensions
      this.toPublish.extensions = this.extensionsWithDescriptions(this.toPublish.extensions)

      if (this.editPublication) {
        // only save meta and upload files
        this.savePromise = Promise.all([
          Hub.updateMeta(conn, this.toPublish),
          ..._.map(this.addedFiles, f => Hub.attach(conn, this.toPublish, f.file))
        ])
      } else {
        // Update version
        this.toPublish.origin.change = this.change.identifier
        this.toPublish.origin.version = this.flow.version

        // Update date of release
        this.toPublish.releaseNote.releasedAt = Date.now()

        this.savePromise = Hub.publish(conn, this.toPublish, this.toPublishContent, {
          flowId: this.flow.id,
          changeId: this.change.identifier
        })

        if (!_.isEmpty(this.addedFiles)) {
          this.savePromise = this.savePromise.then(result => {
            // If the markdown fields contains REPLACE_WITH_IDENTIFIER we do that
            const published = result.body
            this.toPublish = { ...this.toPublish, ...published }
            this.toPublish.description = this.toPublish.description.replaceAll(
              'REPLACE_WITH_IDENTIFIER',
              this.toPublish.identifier
            )
            this.toPublish.releaseNote.note = this.toPublish.releaseNote.note.replaceAll(
              'REPLACE_WITH_IDENTIFIER',
              this.toPublish.identifier
            )
            return Promise.all([
              Hub.updateMeta(conn, this.toPublish),
              ..._.map(this.addedFiles, f => Hub.attach(conn, this.toPublish, f.file))
            ])
          })
        }
      }
      // Notify about success/failure
      this.savePromise
        .then(this.reset)
        .then(() =>
          Toast.success(this.$i18n.t('WAS_PUBLISHED', { thing: this.$i18n.tc('FLOW', 1) }))
        )
        .catch(() =>
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('WAS_NOT_PUBLISHED', { thing: this.$i18n.tc('FLOW', 1) })
            })
          )
        )
    },

    dropFiles(e, editor) {
      this.attachFiles(e)
      // Update editor with image links
      const item = this.toPublish
      const conn = this.connectionFor(item)

      if (editor) {
        _.each(e.dataTransfer.files, f => {
          const link = `![](https://${conn.hub.url}/v1/consumer/attach/${item.origin.hub}/${
            item.type
          }/${item.identifier || 'REPLACE_WITH_IDENTIFIER'}/${encodeURIComponent(f.name)})`
          editor.insertAtCaret(link)
        })
      }
    },

    dropFilesAtDescription(e) {
      this.dropFiles(e, this.$refs.descriptionMarkdown[0])
    },

    dropFilesAtReleaseNote(e) {
      this.dropFiles(e, this.$refs.releaseMarkdown[0])
    },

    attachFiles(e) {
      this.addedFiles = _.uniqBy(
        [
          ..._.map(e.dataTransfer.files, f => {
            return { size: f.size, filename: f.name, file: f }
          }),
          ...this.addedFiles
        ],
        'filename'
      )
    },

    dragover() {
      this.filesOver = true
    },
    dragenter() {
      this.filesOver = true
    },
    dragexit(e) {
      this.filesOver = false
    },
    drop(e) {
      this.attachFiles(e)
      this.filesOver = false
    }
  },

  watch: {
    flow: function(f) {
      Changelog.getLastChange(this.flow.id, 'flow').then(c => {
        this.change = c
      })
    }
  },

  created() {
    // Get the last change for new publications
    Changelog.getLastChange(this.flow.id, 'flow').then(c => {
      this.change = c
    })

    Hub.list()
    this.publicationsPromise = this.reset()

    Flow.readAllFlows()
  }
}
</script>
